import React, { useEffect, useState } from "react";
import { useGlobalContext } from "context/global";
import { ReactComponent as PlaceHolder } from "assets/svgs/image-placeholder.svg";
import Link from "helpers/link";
import { TOGGLE_SUBS_CART } from "utils/action-types";
import { useSelector, useDispatch } from "react-redux";
import Money from "components/money";
import AddToCart from "components/add-to-cart";
import { Swiper, SwiperSlide } from "components/swiper";
import { ReactComponent as Arrow } from "assets/svgs/arrow-down-bold.svg";
import { upsellsRemovedKey } from "utils/constants";
import extractIdFromGid from "helpers/extract-gid";
import AddToSubscriptionCart from "components/add-to-subscription-cart";
import { ReactComponent as Close } from "assets/svgs/close.svg";
import { closedUpsells } from "utils/constants";
import { trackAddUpsellToSubscription } from "services/analytics";
import { closeCart } from "slices/cart";

const UpsellItem = ({ item, isSubscription }) => {
	const { translation } = useGlobalContext();
	const { shopifyTitle, image, handle, variant } = item;
	const dispatch = useDispatch();
	const isBundle = item.type.toLowerCase().includes("bundle");
	const url = isSubscription && isBundle ? `/products/${handle}-sub` : `/products/${handle}`;

	const handleClick = () => {
		if (isSubscription) {
			dispatch({ type: TOGGLE_SUBS_CART });
		}
		else {
			dispatch(closeCart());
		}
	};

	return (
		<div className="flex justify-between items-center gap-x-3 w-full">
			<Link to={url} onClick={handleClick}>
				{image ? <img width="60px" src={image} alt={shopifyTitle} /> :
					<div className="flex justify-center items-center size-15 border rounded-lg border-[#DBDBDB]">
						<PlaceHolder />
					</div>}
			</Link>
			<div className="flex-[1] flex flex-col ml-2.5 text-sm">
				<Link to={url} className="hover:underline" onClick={handleClick}>
					{shopifyTitle}
				</Link>
				<p className="text-sm font-light text-[#757575]">
					{<span className="text-sm">
						<Money value={variant.price} />
					</span>}
				</p>
				{variant.stock === "one-time" && <span className="one-time-badge">{translation.cart?.oneTime}</span>}
			</div>
			{isSubscription ?
				<div className="w-fit">
					<AddToSubscriptionCart
						product={item}
						productVariantId={extractIdFromGid(variant.id)}
						text={translation.product?.add}
						className="upsell-atc !w-fit"
						isUpsell={true}
						callback={() => trackAddUpsellToSubscription({ ...item, isUpsell: true })}
					/> </div> :
				<AddToCart
					text={translation.product?.add} showLoader product={{ ...item, title: shopifyTitle, isUpsell: true }}
					className="upsell-atc !tracking-normal" />
			}
		</div>
	);
};

const CartUpsell = ({ items, config, isSubscription = false }) => {
	const { isClient } = useSelector(state => state.global);
	const { translation } = useGlobalContext();
	const [isClosed, setIsClosed] = useState("true");

	useEffect(() => {
		const closed = sessionStorage.getItem(closedUpsells);
		setIsClosed(closed);
	}, [isClosed]);

	if (!config?.products || !Object.keys(items).length) return null;

	const removedUpsells = isClient ? (JSON.parse(sessionStorage.getItem(upsellsRemovedKey)) || []) : [];
	const products = config.products.filter(product => {
		const variantId = isSubscription ? extractIdFromGid(product.variant.id) : product.variant.id;
		const isRemoved = removedUpsells.includes(variantId);
		const isInCart = Object.hasOwn(items, variantId);
		const isUnavailable = ["unavailable", !isSubscription && "subscription-available"]
			.includes(product.variant.stock);

		return !(isRemoved || isInCart || isUnavailable);
	}).slice(0, config.numberOfProducts);

	const handleClose = () => {
		setIsClosed("true");
		sessionStorage.setItem(closedUpsells, "true");
	};

	if (!products.length || isClosed === "true") return null;

	return (
		<div className="relative mx-6 py-3">
			<div className="w-full flex justify-between border-t py-3">
				<p className="text-sm font-bold">{translation.cart?.you_may_also_like}</p>
				<Close onClick={handleClose} className="w-4 h-4 cursor-pointer" role="button" />
			</div>
			<div className="flex items-center">
				<div className="flex flex-nowrap gap-4 overflow-hidden w-5/6">
					<Swiper
						breakpoints={{ 768: { spaceBetween: 20 } }}
						navigation={{ enabled: true, nextEl: ".ra-upsell", prevEl: ".la-upsell" }}
					>
						{products.map((product, index) => (
							<SwiperSlide key={index} className="w-full">
								<UpsellItem key={product.variant.id} item={product} isSubscription={isSubscription} />
							</SwiperSlide>
						))}
					</Swiper>
				</div>
				<div className="flex gap-3 items-center ml-3 w-4">
					<button className="la-upsell">	<Arrow className="rotate-90" /></button>
					<button className="ra-upsell"> <Arrow className="-rotate-90" /></button>
				</div>
			</div>
		</div>
	);
};

export default CartUpsell;